import { Search, Bell, Plus, Calendar, LogOut, User as UserIcon, ChevronDown } from 'lucide-react';
import { ReactNode, useState, useRef, useEffect } from 'react';
import { useRole } from '../lib/role';
import { useAuth } from '../lib/auth';
import styles from './ProTopBar.module.css';

interface ProTopBarProps {
  /** Mostrato come prima riga sopra il titolo. Sprint 6.A: deprecato, ignored se assente. */
  breadcrumb?: string;
  /** Sprint 6.A: titolo opzionale (per pagine non-dashboard tipo Clienti/Atleti). */
  title?: string;
  /** Label CTA principale. Sprint 6.A: se assente, mostra default role-aware. */
  ctaLabel?: string;
  onCta?: () => void;
  /** Override custom CTA label (override role-aware default). */
  customCtaLabel?: string;
  /** Slot per pulsanti extra a destra del search. */
  rightExtras?: ReactNode;
}

/**
 * Top bar Sprint 6.A — design V2: search role-aware + CTA dinamico nutri/coach.
 * Mantiene retrocompat con la firma legacy (breadcrumb + title), che resta
 * supportata per Clienti/Calendario/Piani esistenti.
 */
export function ProTopBar({
  breadcrumb,
  title,
  ctaLabel,
  onCta,
  customCtaLabel,
  rightExtras,
}: ProTopBarProps) {
  const { role } = useRole();
  const { user, logout } = useAuth();
  const [query, setQuery] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Close dropdown su click fuori
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    setMenuOpen(false);
    await logout();
  };

  const userInitial = user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || '?';
  const userDisplay = user?.studio_name || user?.name || user?.email || 'Account';

  const searchPlaceholder =
    role === 'coach'
      ? 'Cerca atleti, programmi, esercizi…'
      : 'Cerca pazienti, piani, cibi…';

  const defaultCtaLabel =
    role === 'coach' ? 'Nuovo atleta' : 'Nuovo paziente';

  // Backward compat: legacy props (breadcrumb + title) renderizzate sotto la bar.
  const showLegacyHeader = breadcrumb || title;

  return (
    <>
      <header className={styles.bar}>
        <div className={styles.search}>
          <Search size={16} strokeWidth={1.8} style={{ color: 'var(--mute)' }} />
          <input
            type="search"
            className={styles.searchInput}
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={searchPlaceholder}
          />
          <span className={styles.kbd}>⌘ K</span>
        </div>
        <button className={styles.iconBtn} type="button" aria-label="Calendario">
          <Calendar size={16} strokeWidth={1.8} />
        </button>
        <button className={styles.iconBtn} type="button" aria-label="Notifiche">
          <Bell size={16} strokeWidth={1.8} />
          <span className={styles.indicator} aria-hidden />
        </button>
        {rightExtras}
        {(ctaLabel || onCta) && (
          <button className={styles.cta} type="button" onClick={onCta}>
            <Plus size={14} strokeWidth={2.3} />
            <span>{customCtaLabel || ctaLabel || defaultCtaLabel}</span>
          </button>
        )}
        {/* Sprint 9.D — user menu con logout sempre accessibile */}
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="Menu account"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 10px 6px 6px', border: '1px solid var(--line)', borderRadius: 999,
              background: menuOpen ? 'var(--paper-2, #efe9d7)' : 'var(--paper, #fff)',
              cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--ink)',
              minWidth: 40, height: 36,
            }}
          >
            <span aria-hidden style={{
              width: 24, height: 24, borderRadius: '50%',
              background: 'var(--green-deep, #0b8a3e)', color: '#fff',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 11,
            }}>{userInitial}</span>
            <ChevronDown size={12} strokeWidth={2} />
          </button>
          {menuOpen && (
            <div
              role="menu"
              style={{
                position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 50,
                background: 'var(--paper, #fff)', border: '1px solid var(--line)',
                borderRadius: 12, padding: 6, minWidth: 220,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              }}
            >
              <div style={{ padding: '8px 10px 6px', borderBottom: '1px solid var(--line)', marginBottom: 4 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', lineHeight: 1.2 }}>
                  {userDisplay}
                </div>
                {user?.email && (
                  <div style={{ fontSize: 11, color: 'var(--mute)', marginTop: 2 }}>
                    {user.email}
                  </div>
                )}
                {user?.pro_kind && (
                  <div style={{ fontSize: 10, color: 'var(--green-deep)', marginTop: 4, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                    {user.pro_kind === 'both' ? 'Nutri + Coach' : user.pro_kind === 'coach' ? 'Coach' : 'Nutrizionista'}
                  </div>
                )}
              </div>
              <button
                type="button"
                role="menuitem"
                onClick={() => { setMenuOpen(false); window.location.href = '/impostazioni'; }}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 10px', border: 'none', background: 'transparent',
                  cursor: 'pointer', borderRadius: 8, fontSize: 13, color: 'var(--ink)',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--paper-2, #efe9d7)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
              >
                <UserIcon size={14} />
                Impostazioni studio
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={handleLogout}
                disabled={loggingOut}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 10px', border: 'none', background: 'transparent',
                  cursor: loggingOut ? 'wait' : 'pointer', borderRadius: 8, fontSize: 13,
                  color: 'var(--red, #c0392b)', textAlign: 'left', fontWeight: 600,
                }}
                onMouseEnter={(e) => { if (!loggingOut) (e.currentTarget as HTMLButtonElement).style.background = '#fff0f0'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
              >
                <LogOut size={14} />
                {loggingOut ? 'Uscita…' : 'Esci'}
              </button>
            </div>
          )}
        </div>
      </header>
      {showLegacyHeader && (
        <div style={{ padding: '4px 0 18px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {breadcrumb && (
            <div
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: 1.6,
                color: 'var(--mute)',
                textTransform: 'uppercase',
              }}
            >
              {breadcrumb}
            </div>
          )}
          {title && (
            <h1
              style={{
                fontFamily: 'var(--display)',
                fontWeight: 800,
                fontSize: 32,
                letterSpacing: -1,
                margin: 0,
                lineHeight: 1.05,
                color: 'var(--ink)',
              }}
            >
              {title}
            </h1>
          )}
        </div>
      )}
    </>
  );
}
